import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { InboxIcon, XCircleIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { useContent, useDimensionLabel, useLabel, usePercent, useT, useTn } from '@/lib/i18n';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { useModuleName } from '@/hooks/use-module-name';
import { DimensionSpread } from '@/features/analysis/distribution';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

/**
 * Analytics (build/09 §8) — the cohort view that closes the loop with Scoring.
 *
 * Its purpose is norm refresh: per-persona dimension means are what the scoring
 * config's `norms[persona][dimension]` should eventually be set from, so this
 * screen and the config editor are two ends of one workflow.
 *
 * READS THE SAME AGGREGATE THE DASHBOARD DOES, filtered by persona. No second
 * pipeline, no `$lookup`, and the op count does not grow with cohort size.
 *
 * NO MODEL NAMES. `09 §8` asks for "most-common Holland codes"; `hollandCode`
 * lives only on a RIASEC block, so grouping by it would have put a psychometric
 * model into the aggregation. The server groups by `display.headline.value`
 * instead — the render-agnostic projection every score carries, which for RIASEC
 * *is* the Holland code and for a fourth model is whatever that model calls its
 * headline. The screen below therefore works for models that do not exist yet.
 */

const ALL = '__all__';

const STATE_ORDER = ['ready', 'in_progress', 'submitted', 'scoring', 'completed', 'expired', 'flagged'];

export function Analytics() {
  const t = useT();
  const tn = useTn();
  const label = useLabel();
  const percent = usePercent();
  const dimensionLabel = useDimensionLabel();
  const content = useContent();
  const chartConfig = {
    average: { label: t('dashboard.average'), color: 'var(--chart-1)' },
  } satisfies ChartConfig;
  const [personaId, setPersonaId] = useState(ALL);
  const [moduleId, setModuleId] = useState(ALL);

  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const moduleName = useModuleName();


  const params = {
    ...(personaId === ALL ? {} : { personaId }),
    ...(moduleId === ALL ? {} : { moduleId }),
  };
  const agg = useQuery({ queryKey: ['aggregate', params], queryFn: () => api.aggregate(params) });

  const data = agg.data;

  // One instrument at a time: dimension means from different models are
  // different measures, and one axis across them would be meaningless.
  const chartModule = moduleId === ALL ? data?.byDimension[0]?.moduleId : moduleId;
  const moduleDimensions = (data?.byDimension ?? []).filter((d) => d.moduleId === chartModule);
  const dimensions = moduleDimensions
    // The aggregate carries the KEY and no label, so this axis used to read
    // `emotionalStability`. `dimensionLabel` translates the ones we know and
    // leaves an unknown key legible rather than blank.
    .map((d) => ({ name: dimensionLabel(d.key), average: d.average }));

  const funnel = STATE_ORDER
    .map((state) => ({ state, count: data?.instances?.byState?.[state] ?? 0 }))
    .filter((row) => row.count > 0);

  const headlines = (data?.byHeadline ?? []).filter((h) => !chartModule || h.moduleId === chartModule);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.analytics')}
        description={t('analytics.description')}
        actions={
          <div className="flex gap-2">
            <Select value={personaId} onValueChange={setPersonaId}>
              <SelectTrigger className="w-44" aria-label={t('analytics.filterByPersona')}>
                <SelectValue placeholder={t('analytics.allPersonas')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('analytics.allPersonas')}</SelectItem>
                {(personas.data?.items ?? []).map((p) => (
                  <SelectItem key={p.personaId} value={p.personaId}>{content(p.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={moduleId} onValueChange={setModuleId}>
              <SelectTrigger className="w-48" aria-label={t('dashboard.filterByModule')}>
                <SelectValue placeholder={t('dashboard.allModules')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('dashboard.allModules')}</SelectItem>
                {(modules.data?.items ?? []).map((m) => (
                  <SelectItem key={m.moduleId} value={m.moduleId}>{content(m.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {agg.isLoading && (
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-64 w-full" />)}
        </div>
      )}

      {agg.error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('analytics.loadFailed')}
          description={agg.error instanceof Error ? agg.error.message : t('table.errorDescription')}
        />
      )}

      {data && data.results === 0 && (
        <EmptyState
          icon={InboxIcon}
          title={t('analytics.empty.title')}
          description={t('analytics.empty.description')}
        />
      )}

      {data && data.results > 0 && (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <Badge variant="secondary">{tn('analytics.results', data.results)}</Badge>
            <Badge variant="secondary">{tn('candidates.count', data.candidates)}</Badge>
            {data.qcFlags.map((f) => (
              <Badge key={f.flag} variant="outline">{label('qcFlag', f.flag)} {percent(f.rate, 1)}</Badge>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('analytics.dimensionProfile')}</CardTitle>
                <CardDescription>
                  {t('analytics.dimensionProfile.hint', {
                    module: moduleName(chartModule) || t('dashboard.noModule'),
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dimensions.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('analytics.noDimensions')}</p>
                ) : (
                  <ChartContainer config={chartConfig} className="max-h-72 w-full">
                    <BarChart data={dimensions} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid horizontal={false} stroke="var(--border)" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={150}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="average" fill="var(--color-average)" radius={[0, 4, 4, 0]} barSize={18} />
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('analytics.funnel')}</CardTitle>
                <CardDescription>{t('analytics.funnel.hint')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.column.state')}</TableHead>
                      <TableHead className="w-24 text-end">{t('analytics.column.instances')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {funnel.map((row) => (
                      <TableRow key={row.state}>
                        <TableCell>
                          <Badge variant={row.state === 'completed' ? 'default' : 'secondary'}>
                            {label('state', row.state)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-end font-medium">{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-muted-foreground mt-3 text-xs">{t('analytics.funnel.note')}</p>
              </CardContent>
            </Card>

            {/**
              * M2, and this screen is where it earns its keep.
              *
              * Analytics exists for norm refresh — `norms[persona][dimension]`
              * is set from these numbers — and a norm is a mean AND a spread. A
              * console that offered only the mean would have an operator writing
              * half of each norm from the screen and guessing the other half.
              */}
            <div className="lg:col-span-2">
              <DimensionSpread rows={moduleDimensions} />
            </div>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">{t('analytics.headlines')}</CardTitle>
                <CardDescription>{t('analytics.headlines.hint')}</CardDescription>
              </CardHeader>
              <CardContent>
                {headlines.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('analytics.noHeadlines')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {headlines.map((h) => (
                      <Badge key={`${h.moduleId}:${h.headline}`} variant="outline" className="gap-1.5 py-1">
                        <span className="font-medium">{h.headline}</span>
                        <span className="text-muted-foreground">×{h.count}</span>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>


        </>
      )}
    </div>
  );
}
